import React, { useEffect, useRef, useState } from 'react';
import { LuCircleDot, LuShieldCheck, LuUserCog, LuUsersRound } from 'react-icons/lu';
import StatTiles from '../../components/data/StatTiles';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useMediaQuery from '../../hooks/useMediaQuery';
import useViewportFit from '../../hooks/useViewportFit';
import useAccessText from './accessText';
import usePageAccess from './usePageAccess';
import UsageStatsModal from './UsageStatsModal';
import AccessEditor from './parts/AccessEditor';
import { MessageDialog, PasswordDialog, PeopleDialog } from './parts/AccessDialogs';
import EmployeeList from './parts/EmployeeList';
import PageAccessHeader from './parts/PageAccessHeader';

const WIDE_QUERY = '(min-width: 1024px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 520;

/**
 * Sayfa Yetkileri (Tailwind): head-count tiles that open the admin /
 * restricted admin / online lists, the employees on the left and the
 * selected person's pages on the right, both reaching the bottom of the
 * screen on wide displays. Phones show the list, then the editor with a back
 * button. All data and actions live in usePageAccess.
 */
const PageAccessManagementPage = () => {
  const access = usePageAccess();
  const { language, label } = useAccessText();
  const wide = useMediaQuery(WIDE_QUERY);
  const areaRef = useRef(null);
  const fitHeight = useViewportFit(areaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const [peopleKind, setPeopleKind] = useState(null);
  const [showStats, setShowStats] = useState(false);

  const {
    loading, employees, selectedEmployee, onlineUsers, onlineEmployees, onlineActivity,
    adminEmployees, restrictedAdminEmployees,
  } = access;

  const peopleOf = { admins: adminEmployees, restricted: restrictedAdminEmployees, online: onlineEmployees };
  const tiles = [
    { key: 'employees', label: label('tiles.employees'), shortLabel: label('tiles.employeesShort'), value: employees.length, icon: LuUsersRound, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30' },
    { key: 'online', label: label('tiles.online'), shortLabel: label('tiles.onlineShort'), value: onlineEmployees.length, icon: LuCircleDot, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', onClick: () => setPeopleKind('online') },
    { key: 'admins', label: label('tiles.admins'), shortLabel: label('tiles.adminsShort'), value: adminEmployees.length, icon: LuShieldCheck, gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30', onClick: () => setPeopleKind('admins') },
    { key: 'restricted', label: label('tiles.restricted'), shortLabel: label('tiles.restrictedShort'), value: restrictedAdminEmployees.length, icon: LuUserCog, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', onClick: () => setPeopleKind('restricted') },
  ];

  // Picking someone in a list window opens their pages (asking first if the draft is unsaved)
  const openFromList = (emp) => {
    setPeopleKind(null);
    access.requestSelect(emp);
  };

  // Phones swap the list for the editor: bring the editor's top into view
  const selectedId = selectedEmployee?.id;
  useEffect(() => {
    if (wide || !areaRef.current) return;
    const top = areaRef.current.getBoundingClientRect().top + window.scrollY - 80;
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
  }, [selectedId, wide]);

  const showList = wide || !selectedEmployee;
  const showEditor = wide || Boolean(selectedEmployee);

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageAccessHeader
          showPassive={access.showPassiveEmployees}
          passiveCount={access.passiveCount}
          onTogglePassive={() => access.setShowPassiveEmployees((prev) => !prev)}
          onStats={() => setShowStats(true)}
        />

        <StatTiles tiles={tiles} ready={!loading} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div
          ref={areaRef}
          style={wide && fitHeight ? { height: fitHeight } : undefined}
          className="tw-grid tw-gap-4 lg:tw-grid-cols-[22rem_minmax(0,1fr)] 2xl:tw-grid-cols-[24rem_minmax(0,1fr)]"
        >
          {showList && (
            <EmployeeList
              loading={loading}
              employees={access.filteredEmployees}
              total={employees.length}
              onlineUsers={onlineUsers}
              onlineActivity={onlineActivity}
              accessibleCount={access.accessibleCount}
              search={access.employeeSearch}
              onSearch={access.setEmployeeSearch}
              filter={access.listFilter}
              onFilter={access.setListFilter}
              onlineCount={onlineEmployees.length}
              grantedCount={access.grantedCount}
              selectedId={selectedEmployee?.id}
              onSelect={access.requestSelect}
            />
          )}
          {showEditor && (
            <AccessEditor
              employee={selectedEmployee}
              roles={access.selectedRoles}
              online={selectedEmployee ? onlineUsers.has(selectedEmployee.id) : undefined}
              allPages={access.allPages}
              draftGrants={access.draftGrants}
              accessibleCount={access.draftAccessibleCount}
              pageSearch={access.pageSearch}
              onPageSearch={access.setPageSearch}
              pageFilter={access.pageFilter}
              onPageFilter={access.setPageFilter}
              onToggle={access.toggleGrant}
              onToggleGroup={access.toggleGroup}
              onPassword={access.openPasswordModal}
              hasChanges={access.hasChanges}
              changeCount={access.changeCount}
              saving={access.saving}
              onReset={access.resetDraft}
              onSave={access.handleSave}
              showBack={!wide}
              onBack={() => access.requestSelect(null)}
            />
          )}
        </div>
      </div>

      <PeopleDialog
        kind={peopleKind}
        people={peopleKind ? peopleOf[peopleKind] : []}
        loading={loading}
        onlineActivity={onlineActivity}
        onClose={() => setPeopleKind(null)}
        onSelect={openFromList}
        onMessage={access.openMessage}
      />

      <MessageDialog
        target={access.messageTarget}
        text={access.messageText}
        sending={access.sendingMessage}
        onText={access.setMessageText}
        onSend={access.sendMessage}
        onClose={() => !access.sendingMessage && access.setMessageTarget(null)}
      />

      <PasswordDialog
        isOpen={access.showPasswordModal}
        employee={selectedEmployee}
        password={access.newPassword}
        sendEmail={access.sendEmail}
        saving={access.pwSaving}
        error={access.pwError}
        onPassword={access.setNewPassword}
        onGenerate={access.fillGeneratedPassword}
        onSendEmail={access.setSendEmail}
        onSubmit={access.handleSetPassword}
        onClose={() => !access.pwSaving && access.setShowPasswordModal(false)}
      />

      <ConfirmDialog
        isOpen={Boolean(access.pendingSelection)}
        title={label('switchConfirm.title')}
        message={label('switchConfirm.message', { name: selectedEmployee ? `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim() : '' })}
        confirmLabel={label('switchConfirm.confirm')}
        cancelLabel={label('switchConfirm.cancel')}
        tone="warning"
        onConfirm={access.confirmPendingSelection}
        onCancel={() => access.setPendingSelection(null)}
      />

      <UsageStatsModal
        isOpen={showStats}
        employees={employees}
        onlineUsers={onlineUsers}
        showPassiveEmployees={access.showPassiveEmployees}
        onClose={() => setShowStats(false)}
      />
    </>
  );
};

export default PageAccessManagementPage;
